// PeptideVault 9.0.0 — deterministic preparation calculations.
// AI may extract/review inputs; inventory arithmetic is always deterministic.

function preparationUnit(unit) {
  const value = String(unit || '').trim().toLowerCase().replace(/\s+/g, '')
  if (['mcg','μg','ug'].includes(value)) return 'mcg'
  if (value === 'mg') return 'mg'
  if (['iu','i.u.','internationalunits'].includes(value)) return 'IU'
  return String(unit || '').trim()
}

function convertPreparationAmount(amount, fromUnit, toUnit) {
  const value = Number(amount)
  const from = preparationUnit(fromUnit)
  const to = preparationUnit(toUnit)
  if (!Number.isFinite(value) || !from || !to) return null
  if (from === to) return value
  if (from === 'mcg' && to === 'mg') return value / 1000
  if (from === 'mg' && to === 'mcg') return value * 1000
  return null
}

function selectedSkuStrength(sku) {
  if (!sku) return null
  const explicit = Number(sku.vialStrength)
  const explicitUnit = preparationUnit(sku.vialStrengthUnit)
  if (explicit > 0 && ['mcg','mg','IU'].includes(explicitUnit)) return { amount:explicit, unit:explicitUnit }
  const legacy = Number(sku.vialStrengthMg || sku.mgPerVial)
  return legacy > 0 ? { amount:legacy, unit:'mg' } : null
}

function finiteProtocolRequirements(protocol, events, selectedSkus) {
  if (!protocol || protocol.isOngoing || protocol.ongoing || (!protocol.endDate && protocol.scheduleMode !== 'explicit')) {
    return { protocolId:protocol && protocol.id || '', finite:false, reason:'ongoing', compounds:[] }
  }
  const groups = new Map()
  ;(events || []).forEach(event => {
    const compoundId = event.peptideId || event.compoundId || event.peptideName || event.compoundName || 'unmapped'
    const compoundName = event.peptideName || event.compoundName || 'Unmapped compound'
    const planned = event.planningDose || { amount:event.doseAmount, unit:event.doseUnit }
    const sourceUnit = preparationUnit(planned && planned.unit)
    const unit = sourceUnit === 'mcg' ? 'mg' : sourceUnit
    const rawAmount = Number(planned && planned.amount)
    const amount = sourceUnit === 'mcg' ? convertPreparationAmount(rawAmount, 'mcg', 'mg') : rawAmount
    const key = `${compoundId}::${unit}`
    if (!(amount > 0) || !unit) {
      const invalidKey = `${compoundId}::unknown`
      if (!groups.has(invalidKey)) groups.set(invalidKey, { compoundId, compoundName, unit:'', amount:0, doseCount:0, issues:[] })
      groups.get(invalidKey).issues.push('Missing or unsupported dose unit')
      return
    }
    if (!groups.has(key)) groups.set(key, { compoundId, compoundName, unit, amount:0, doseCount:0, issues:[] })
    const row = groups.get(key)
    row.amount += amount
    row.doseCount++
  })

  const compounds = [...groups.values()].map(group => {
    group.amount = Number(group.amount.toFixed(group.unit === 'mg' ? 6 : 3))
    const sku = selectedSkus && (selectedSkus[group.compoundId] || selectedSkus[group.compoundName])
    const strength = selectedSkuStrength(sku)
    if (!sku) group.issues.push('Select a SKU')
    else if (!strength) group.issues.push('Selected SKU has no vial strength')
    const compatibleStrength = strength ? convertPreparationAmount(strength.amount, strength.unit, group.unit) : null
    if (strength && compatibleStrength == null) group.issues.push(`Cannot convert ${strength.unit} to ${group.unit}`)
    return { ...group, skuId:sku && sku.id || '', skuStrength:strength, requiredVials:compatibleStrength > 0 ? Math.ceil(group.amount / compatibleStrength) : null }
  })
  return { protocolId:protocol.id || '', finite:true, startDate:protocol.startDate || '', endDate:protocol.endDate || '', compounds }
}

function packServingForSlot(supplement, person, slot) {
  if (!supplement) return 0
  if (supplement.takenBy && supplement.takenBy !== 'both' && supplement.takenBy !== person) return 0
  if (supplement.userDosages && supplement.dosagesDifferent && supplement.takenBy === 'both') {
    const value = supplement.userDosages[person] && supplement.userDosages[person][slot]
    return Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0
  }
  if (supplement.timeOfDay === 'both') {
    if (slot === 'am') return Math.max(0, Number(supplement.servingAM ?? supplement.dailyServing ?? 1))
    if (slot === 'pm') return Math.max(0, Number(supplement.servingPM ?? supplement.dailyServing ?? 1))
    return 0
  }
  return supplement.timeOfDay === slot ? Math.max(0, Number(supplement.dailyServing ?? 1)) : 0
}

function selectedPackCapacity(supplements, person, slots) {
  const selectedSlots = Array.isArray(slots) ? slots : [slots]
  const rows = (supplements || []).map(supplement => {
    const pillsPerPack = selectedSlots.reduce((sum, slot) => sum + packServingForSlot(supplement, person, slot), 0)
    if (!(pillsPerPack > 0)) return null
    const stockKnown = supplement.quantityOnHand !== '' && supplement.quantityOnHand !== null && supplement.quantityOnHand !== undefined && Number.isFinite(Number(supplement.quantityOnHand))
    const available = stockKnown ? Math.max(0, Number(supplement.quantityOnHand)) : null
    return { supplementId:supplement.id, name:supplement.name || 'Supplement', pillsPerPack, available, capacity:stockKnown ? Math.floor(available / pillsPerPack) : null, stockKnown }
  }).filter(Boolean)
  const known = rows.filter(row => row.stockKnown)
  const maxPacks = rows.length && known.length === rows.length ? Math.min(...known.map(row => row.capacity)) : null
  return { person, slots:selectedSlots, maxPacks, limitingSupplementIds:maxPacks == null ? [] : rows.filter(row => row.capacity === maxPacks).map(row => row.supplementId), incomplete:known.length !== rows.length, rows }
}

function batchDeductionPlan(capacityResult, packCount) {
  const count = Math.floor(Number(packCount))
  if (!(count > 0)) return { ok:false, reason:'Enter a batch size' }
  if (!capacityResult || capacityResult.incomplete) return { ok:false, reason:'Stock is unknown for one or more supplements' }
  if (count > capacityResult.maxPacks) return { ok:false, reason:`Only ${capacityResult.maxPacks} complete pack${capacityResult.maxPacks === 1 ? '' : 's'} can be prepared` }
  return { ok:true, packCount:count, deductions:capacityResult.rows.map(row => ({ supplementId:row.supplementId, quantity:row.pillsPerPack * count })) }
}

function combinedPickingRoute(rows) {
  const groups = new Map()
  ;(rows || []).forEach(row => {
    const key = [row.peptideId,row.skuId,row.strengthAmount,row.strengthUnit,row.positionId || row.locationCode].join('::')
    if (!groups.has(key)) groups.set(key, { peptideId:row.peptideId || '', peptideName:row.peptideName || '', skuId:row.skuId || '', skuLabel:row.skuLabel || '', strengthAmount:row.strengthAmount, strengthUnit:row.strengthUnit || '', positionId:row.positionId || '', locationCode:row.locationCode || '', quantity:0, allocations:[] })
    const group = groups.get(key)
    const quantity = Math.max(0, Number(row.qty != null ? row.qty : row.quantity) || 0)
    group.quantity += quantity
    group.allocations.push({ protocolId:row.protocolId || '', protocolName:row.protocolName || '', quantity })
  })
  return [...groups.values()].sort((a,b) => String(a.locationCode).localeCompare(String(b.locationCode), undefined, { numeric:true }) || String(a.peptideName).localeCompare(String(b.peptideName)))
}

export { preparationUnit, convertPreparationAmount, selectedSkuStrength, finiteProtocolRequirements, packServingForSlot, selectedPackCapacity, batchDeductionPlan, combinedPickingRoute }
